import { LOCALIZED_PAGES, LOCALIZED_SLUGS, type TranslatedLocale } from '~/constants/localizedRoutes'

/**
 * The languages the site is shown in. A few pages whose content is English
 * only stay English; middleware/locale.global.ts picks per page.
 */
export const APP_LOCALES = ['en', 'cs', 'de', 'pl'] as const
export type AppLocale = typeof APP_LOCALES[number]

/**
 * English has no URL prefix; every other language lives under its own
 * (/cs/pricing, /de/pricing, /pl/cennik). Only the marketing pages are localized like this; the app and
 * the blog have one URL each (`defineI18nRoute(false)` in the page).
 */
export const DEFAULT_LOCALE: AppLocale = 'en'

/** The visitor's choice, or the language their browser asked for on the first visit. */
export const LOCALE_COOKIE = 'imr_locale'

export function isAppLocale(value: unknown): value is AppLocale {
  return typeof value === 'string' && (APP_LOCALES as readonly string[]).includes(value)
}

const PREFIX_RE = new RegExp(`^/(${APP_LOCALES.filter(l => l !== DEFAULT_LOCALE).join('|')})(?=/|$|[?#])`)

/** "/cs/pricing" → "cs", "/de/pricing" → "de", "/pl/cennik" → "pl", "/pricing" → "en". */
export function localeFromPath(path: string): AppLocale {
  const match = PREFIX_RE.exec(path)
  return match && isAppLocale(match[1]) ? match[1] : DEFAULT_LOCALE
}

/** "/cs/cenik" → "/cenik", "/cs" → "/": the prefix only, the rest as it is. */
export function stripLocalePrefix(path: string): string {
  const stripped = path.replace(PREFIX_RE, '')
  return !stripped ? '/' : stripped[0] === '/' ? stripped : `/${stripped}`
}

function isTranslated(locale: AppLocale): locale is TranslatedLocale {
  return locale !== DEFAULT_LOCALE
}

/** "/pricing/?x=1#faq" → ["/pricing", "?x=1#faq"]. */
function splitSuffix(path: string): [string, string] {
  const index = path.search(/[?#]/)
  const [bare, suffix] = index === -1 ? [path, ''] : [path.slice(0, index), path.slice(index)]
  return [bare.length > 1 ? bare.replace(/\/+$/, '') : bare, suffix]
}

/** The hub a generated page sits under, and its slug: "/resume-examples/teacher" → ["/resume-examples", "teacher"]. */
function splitSlug(path: string): [string, string] | null {
  const index = path.lastIndexOf('/')
  const hub = path.slice(0, index)
  return index > 0 && hub in LOCALIZED_SLUGS ? [hub, path.slice(index + 1)] : null
}

/**
 * The English path of the page a URL shows, in any language: "/cs/cenik" →
 * "/pricing", "/de/lebenslauf-beispiele/teacher" → "/resume-examples/teacher",
 * "/cs" → "/". For code that recognises pages by their path, so a translated
 * page counts as the page it translates. The English slugs under /cs and /de
 * (the URLs before translation) come back English too.
 */
export function defaultLocalePath(path: string): string {
  const locale = localeFromPath(path)
  const [bare, suffix] = splitSuffix(stripLocalePrefix(path))
  if (!isTranslated(locale)) return bare + suffix
  const page = Object.keys(LOCALIZED_PAGES).find(en => LOCALIZED_PAGES[en]![locale] === bare)
  if (page) return page + suffix
  const index = bare.lastIndexOf('/')
  const hubPath = bare.slice(0, index)
  const hub = index > 0 ? Object.keys(LOCALIZED_SLUGS).find(en => en === hubPath || LOCALIZED_PAGES[en]?.[locale] === hubPath) : undefined
  if (!hub) return bare + suffix
  const slug = bare.slice(index + 1)
  const slugs = LOCALIZED_SLUGS[hub]!
  const en = Object.keys(slugs).find(key => slugs[key]![locale] === slug)
  return `${hub}/${en ?? slug}${suffix}`
}

/**
 * The English slug of a page generated under a hub, whatever the language of
 * its URL: "/cs/priklady-zivotopisu/ucitel" → "teacher". The data is keyed by it.
 */
export function defaultLocaleSlug(path: string): string {
  return splitSuffix(defaultLocalePath(path))[0].split('/').pop() ?? ''
}

/**
 * Where the page with this English path lives in a language: "/pricing" →
 * "/cs/cenik", "/resume-examples/teacher" → "/cs/priklady-zivotopisu/ucitel".
 * Pages without a translated path only get the prefix ("/cookies" →
 * "/cs/cookies"). Says nothing about whether the page has a URL per language;
 * composables/useLocalizedPath.ts checks that.
 */
export function localizedPath(englishPath: string, locale: AppLocale): string {
  const [bare, suffix] = splitSuffix(englishPath)
  if (!isTranslated(locale)) return bare + suffix
  const page = LOCALIZED_PAGES[bare]?.[locale]
  if (page) return `/${locale}${page}${suffix}`
  const slugged = splitSlug(bare)
  if (slugged) {
    const [hub, slug] = slugged
    const translated = LOCALIZED_SLUGS[hub]![slug]?.[locale] ?? slug
    return `/${locale}${LOCALIZED_PAGES[hub]?.[locale] ?? hub}/${translated}${suffix}`
  }
  return `/${locale}${bare === '/' ? '' : bare}${suffix}`
}

/**
 * Whether the page with this English path has its own text in a language. The
 * pages generated under a hub (/resume-examples/teacher, LOCALIZED_SLUGS) have
 * none in Polish yet, and in German only the resume examples with a German
 * slug do (data/resume-examples/de): elsewhere they show the English text, so
 * they are neither listed nor indexed in that language (app.vue, the sitemap).
 */
export function hasOwnContent(englishPath: string, locale: AppLocale): boolean {
  const slugged = splitSlug(splitSuffix(englishPath)[0])
  if (!slugged) return true
  if (locale === 'pl') return false
  const [hub, slug] = slugged
  if (locale === 'de' && hub === '/resume-examples') return Boolean(LOCALIZED_SLUGS[hub]![slug]?.de)
  return true
}

/**
 * The app language for a browser's language list, in its order of preference:
 * Czech for Czech and Slovak (a Slovak reader follows Czech far better than
 * English), German for German (Germany, Austria, Switzerland, Liechtenstein,
 * Luxembourg alike), English for English, and English when nothing matches.
 */
export function localeFromLanguages(languages: readonly string[]): AppLocale {
  for (const language of languages) {
    const base = language.trim().toLowerCase().split(/[-_;]/)[0]
    if (base === 'cs' || base === 'sk') return 'cs'
    if (base === 'de') return 'de'
    if (base === 'pl') return 'pl'
    if (base === 'en') return 'en'
  }
  return 'en'
}

/** "cs-CZ,cs;q=0.9,en;q=0.8" → ["cs-CZ", "cs", "en"], by q value. */
export function parseAcceptLanguage(header: string | undefined): string[] {
  return (header || '')
    .split(',')
    .map((part, index) => {
      const [tag, ...params] = part.trim().split(';')
      const q = params.map(p => p.trim()).find(p => p.startsWith('q='))
      return { tag: (tag || '').trim(), q: q ? Number(q.slice(2)) || 0 : 1, index }
    })
    .filter(entry => entry.tag && entry.tag !== '*' && entry.q > 0)
    .sort((a, b) => b.q - a.q || a.index - b.index)
    .map(entry => entry.tag)
}

/**
 * The text for a locale from one per language, English for anything unknown.
 * For server-rendered e-mails and pages that do not go through vue-i18n.
 */
export function byLocale<T>(locale: string | null | undefined, texts: Record<AppLocale, T>): T {
  return texts[isAppLocale(locale) ? locale : DEFAULT_LOCALE]
}

/** BCP 47 tag for dates in a locale's own format. */
export const DATE_LOCALES: Record<AppLocale, string> = { en: 'en-GB', cs: 'cs-CZ', de: 'de-DE', pl: 'pl-PL' }
